import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { Field, Notice } from '../components/ui/misc'
import { AuthLayout, Divider, GoogleButton } from '../pages/auth/AuthLayout'
import { adminAuthClient, useAdminSession } from './session'

/** Staff sign-in. Only ADMIN_EMAILS get a session here; nobody can sign up. */
export function AdminLogin() {
  const qc = useQueryClient()
  const [params] = useSearchParams()
  const { data: session } = useAdminSession()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(params.get('error') ? "That account can't sign in here." : null)

  if (session?.user) return <Navigate to="/" replace />

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    const { error: err } = await adminAuthClient.signIn.email({ email, password })
    setBusy(false)
    if (err) return setError(err.code === 'NOT_AN_ADMIN' ? 'This account is not an admin.' : (err.message ?? 'Could not sign in'))
    await qc.invalidateQueries({ queryKey: ['admin-session'] })
  }

  return (
    <AuthLayout title="Admin" subtitle="Trodo Console staff only. Sign in with your admin account." product="Admin" pitch={false}>
      {error && (
        <div className="mb-4">
          <Notice tone="warning">{error}</Notice>
        </div>
      )}
      {session?.google && (
        <>
          <GoogleButton onClick={() => adminAuthClient.signIn.social({ provider: 'google', callbackURL: '/', errorCallbackURL: '/login' })} />
          <Divider />
        </>
      )}
      <form onSubmit={submit} className="space-y-3">
        <Field label="Email">
          <input className="input" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Password">
          <input className="input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <Button type="submit" className="h-10 w-full" loading={busy}>
          Sign in
        </Button>
      </form>
    </AuthLayout>
  )
}
